// UserForm.js
import React, { useState, useContext } from "react";
import { createPortal } from "react-dom";
import {
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiFieldPassword,
  EuiButton,
  EuiButtonEmpty,
  EuiSpacer,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiText,
  EuiCallOut,
} from "@elastic/eui";
import { getApiUrl } from "../utils/api";
import { UserContext } from "../Context";
import { useNavigate } from "react-router-dom";
import { routeStudent } from "../utils/studentRouting";
import TARouteSelector from "../pages/TARouteSelector";

const UserForm = ({ onSwitchToRegister }) => {
  const [form, setForm] = useState({
    username: "",
    password: "",
  });
  const { user, setUser } = useContext(UserContext);
  const [isLoading, setisLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [forgotPasswordUsername, setForgotPasswordUsername] = useState("");
  const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] =
    useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [showTARoleSelector, setShowTARoleSelector] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);

  if (user && !showTARoleSelector) {
    navigate("/");
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordUsername.trim()) {
      setForgotPasswordError("Please enter your username");
      return;
    }

    setForgotPasswordError(null);
    setForgotPasswordLoading(true);

    try {
      // Verify that user exists
      const verifyResponse = await fetch(
        getApiUrl("/api/auth/verify-ta-reset-code"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: forgotPasswordUsername,
            reset_code: "dummy", // Will fail but tells us if user exists
          }),
        }
      );

      const verifyData = await verifyResponse.json();

      // If we get "User not found", show error
      if (verifyData.message === "User not found") {
        setForgotPasswordError("Username not found");
        setForgotPasswordLoading(false);
        return;
      }

      // Otherwise, proceed to reset code entry screen
      setForgotPasswordSuccess(true);
      setNewPassword("");
      setConfirmPassword("");
      setOtp("");
    } catch (error) {
      setForgotPasswordError("Error verifying username");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setForgotPasswordError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setForgotPasswordError("Password must be at least 8 characters long");
      return;
    }

    setForgotPasswordError(null);
    setResetPasswordLoading(true);

    try {
      // Reset password with TA code
      const resetResponse = await fetch(
        getApiUrl("/api/auth/reset-password-with-ta-code"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: forgotPasswordUsername,
            reset_code: otp,
            new_password: newPassword,
          }),
        }
      );

      const resetData = await resetResponse.json();

      if (!resetResponse.ok || resetData.status === "error") {
        throw new Error(resetData.message || "Failed to reset password");
      }

      // Success - close modal and show success message
      closeForgotPasswordModal();
      alert(
        "Password reset successfully! You can now login with your new password."
      );
    } catch (error) {
      setForgotPasswordError(error.message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setIsForgotPasswordModalVisible(false);
    setForgotPasswordUsername("");
    setForgotPasswordError(null);
    setForgotPasswordSuccess(false);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleTARoleSelection = async (selectedRole) => {
    if (!pendingUserData) return;

    const updatedUser = {
      ...pendingUserData,
      effectiveRole: selectedRole,
    };

    // Navigate BEFORE updating state to avoid early return redirect
    if (selectedRole === "manager") {
      sessionStorage.setItem("ta_manager_mode", "true");
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      navigate("/courses");
    } else {
      sessionStorage.removeItem("ta_manager_mode");
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      await routeStudent(navigate);
    }
    
    setShowTARoleSelector(false);
    setPendingUserData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setisLoading(true);

    try {
      const response = await fetch(getApiUrl("/api/login"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: form.username,
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      setisLoading(false);

      // Check if user is a student (or has no role/empty role)
      const isStudent =
        data.role === "student" || data.role === "" || !data.role;

      if (isStudent && data.isTA) {
        // User is both a student and a TA - show role selector
        setPendingUserData(data);
        setShowTARoleSelector(true);
        return;
      }

      // Not a TA, proceed with normal login flow
      setUser(data);
      sessionStorage.setItem("user", JSON.stringify(data));
      sessionStorage.removeItem("ta_manager_mode");

      if (isStudent) {
        await routeStudent(navigate);
      } else {
        navigate("/courses");
      }
    } catch (error) {
      console.error("There was an error!", error);
      setError(error.message);
      setisLoading(false);
    }
  };

  if (showTARoleSelector) {
    return createPortal(
      <TARouteSelector
        onSelectRole={handleTARoleSelection}
        navigate={navigate}
      />,
      document.body
    );
  }

  return (
    <>
      <EuiForm
        component="form"
        onSubmit={handleSubmit}
        isInvalid={!!error}
        error={error}
      >
        <EuiSpacer size="s" />
        <EuiFormRow label="Email">
          <EuiFieldText
            name="username"
            value={form.username}
            onChange={handleChange}
            className="modern-input"
          />
        </EuiFormRow>

        <EuiFormRow label="Password">
          <EuiFieldPassword
            name="password"
            value={form.password}
            onChange={handleChange}
            className="modern-input"
          />
        </EuiFormRow>
        <EuiSpacer size="m" />

        <EuiButton
          type="submit"
          fill
          isLoading={isLoading}
          className="modern-button"
          fullWidth
        >
          Login
        </EuiButton>
      </EuiForm>

      <div className="auth-link-group">
        <EuiButtonEmpty
          size="s"
          className="auth-link"
          onClick={() => setIsForgotPasswordModalVisible(true)}
        >
          Forgot your password?
        </EuiButtonEmpty>
        {onSwitchToRegister && (
          <EuiButtonEmpty
            size="s"
            className="auth-link"
            onClick={onSwitchToRegister}
          >
            Need an account? Register
          </EuiButtonEmpty>
        )}
      </div>

      {isForgotPasswordModalVisible && (
        <EuiModal onClose={closeForgotPasswordModal}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              {forgotPasswordSuccess
                ? "Enter Reset Code & New Password"
                : "Reset Your Password"}
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            {!forgotPasswordSuccess ? (
              <>
                <EuiText>
                  <p>
                    Enter your username to reset your password. You'll need the
                    reset code provided by your instructor.
                  </p>
                </EuiText>
                <EuiSpacer size="m" />
                <EuiFormRow
                  label="Username"
                  isInvalid={!!forgotPasswordError}
                  error={forgotPasswordError}
                >
                  <EuiFieldText
                    value={forgotPasswordUsername}
                    onChange={(e) => setForgotPasswordUsername(e.target.value)}
                    placeholder="Enter your username"
                  />
                </EuiFormRow>
              </>
            ) : (
              <>
                <EuiText>
                  <p>
                    Enter the reset code from your instructor for{" "}
                    <strong>{forgotPasswordUsername}</strong> and your new
                    password.
                  </p>
                </EuiText>
                <EuiSpacer size="m" />
                {forgotPasswordError && (
                  <>
                    <EuiCallOut title="Error" color="danger" iconType="alert">
                      <p>{forgotPasswordError}</p>
                    </EuiCallOut>
                    <EuiSpacer size="m" />
                  </>
                )}
                <EuiFormRow label="Reset Code">
                  <EuiFieldText
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.toUpperCase())}
                    placeholder="Enter 6-digit code from instructor"
                    maxLength={6}
                  />
                </EuiFormRow>
                <EuiFormRow label="New Password">
                  <EuiFieldPassword
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                  />
                </EuiFormRow>
                <EuiFormRow label="Confirm Password">
                  <EuiFieldPassword
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </EuiFormRow>
              </>
            )}
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButton onClick={closeForgotPasswordModal} color="text">
              Cancel
            </EuiButton>
            {!forgotPasswordSuccess ? (
              <EuiButton
                onClick={handleForgotPassword}
                fill
                isLoading={forgotPasswordLoading}
                disabled={!forgotPasswordUsername.trim()}
              >
                Continue
              </EuiButton>
            ) : (
              <EuiButton
                onClick={handleResetPassword}
                fill
                isLoading={resetPasswordLoading}
                disabled={
                  !otp.trim() || !newPassword.trim() || !confirmPassword.trim()
                }
              >
                Reset Password
              </EuiButton>
            )}
          </EuiModalFooter>
        </EuiModal>
      )}
    </>
  );
};

export default UserForm;
